import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Minus, Trash2 } from "lucide-react";
import { useLang, useT } from "../../i18n";
import { addToCart, deleteProduct } from "../../lib/shopApi";
import { toast } from "../../components/ui/sonner";

export const ProductCard = ({ product, onChanged }) => {
  const t = useT();
  const lang = useLang();
  const [sizeLabel, setSizeLabel] = useState(product.sizes[0]?.label || "");
  const [qty, setQty] = useState(1);
  const size = product.sizes.find((s) => s.label === sizeLabel) || product.sizes[0];

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-cream-50 border border-bergamote-600/15 rounded-3xl overflow-hidden flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      {product.image ? (
        <img src={product.image} alt={product.name_fr} className="w-full h-44 object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-44 bg-bergamote-100 flex items-center justify-center font-serif text-bergamote-400 text-2xl">la bergamote</div>
      )}
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          <h3 className="font-serif text-xl font-semibold text-bergamote-900">{product[lang === "fr" ? "name_fr" : "name_en"]}</h3>
          <p className="text-sm text-bergamote-800/70 mt-1">{product[lang === "fr" ? "desc_fr" : "desc_en"]}</p>
        </div>

        {product.sizes.length > 1 && (
          <div className="flex flex-wrap gap-2" data-testid={`size-select-${product.id}`}>
            {product.sizes.map((s) => (
              <button
                key={s.label}
                data-testid={`size-${product.id}-${s.label}`}
                onClick={() => setSizeLabel(s.label)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                  s.label === sizeLabel
                    ? "bg-bergamote-600 text-cream-50 border-bergamote-600"
                    : "border-bergamote-600/30 text-bergamote-700 hover:border-bergamote-600"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
        {product.sizes.length === 1 && (
          <div className="flex flex-wrap gap-2" data-testid={`size-select-${product.id}`}>
            <span
              data-testid={`size-${product.id}-${product.sizes[0].label}`}
              className="px-3 py-1.5 rounded-full text-xs font-semibold border border-bergamote-600/30 text-bergamote-700"
            >
              {product.sizes[0].label}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-2">
          <span className="font-mono text-bergamote-900 font-semibold" data-testid={`price-${product.id}`}>
            CHF {size?.price.toFixed(2)}
          </span>
          <div className="flex items-center gap-2">
            <button data-testid={`qty-minus-${product.id}`} onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-7 h-7 rounded-full border border-bergamote-600/30 flex items-center justify-center text-bergamote-700 hover:bg-bergamote-100">
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span className="w-6 text-center font-mono text-sm" data-testid={`qty-${product.id}`}>{qty}</span>
            <button data-testid={`qty-plus-${product.id}`} onClick={() => setQty((q) => Math.min(10, q + 1))} className="w-7 h-7 rounded-full border border-bergamote-600/30 flex items-center justify-center text-bergamote-700 hover:bg-bergamote-100">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={() => {
            addToCart(product, size.label, qty);
            toast.success(t.sh_added);
          }}
          className="w-full rounded-full bg-bergamote-600 text-cream-50 py-3 text-sm font-semibold hover:bg-bergamote-700 transition-colors duration-300"
        >
          {t.sh_add}
        </button>
        <button
          data-testid={`delete-product-${product.id}`}
          onClick={async () => {
            await deleteProduct(product.id);
            onChanged();
          }}
          className="text-[11px] font-mono uppercase tracking-widest text-bergamote-400 hover:text-destructive transition-colors inline-flex items-center gap-1 self-center"
        >
          <Trash2 className="w-3 h-3" /> {t.sh_delete}
        </button>
      </div>
    </motion.article>
  );
};
